import { makeAutoObservable, runInAction } from 'mobx'
import { generatePath } from 'react-router'
import { studentController } from '@/controller/student.controller'
import type { StudentGroups } from 'src/dto'
import { GroupInfo, SchoolGrade } from 'src/dto'
import { RouteName } from 'src/router'

import commonRepo from '@/repository/common.repo'

// StudentGroupAccordionService 에서 준 선택된 학생, 반 정보를 관리하는 서비스
// TODO - 출결, 교육비 둘다 쓰는 서비스

export class AttendanceGroupService {
  studentGroups?: StudentGroups // 학년 - 반 정보
  viewType: 'calendar' | 'chart' = 'calendar'
  groupLastPath = new Map<GroupInfo['key'], string>()
  urlGroupInfo?: GroupInfo // 현재 url에 맞는 (초중고)-(학년, 반, 학생) 정보

  constructor() {
    makeAutoObservable(this)
  }

  // url에 따른 현재 학생 정보 (반, 학년, 선택한 학생정보)
  get groupStudents() {
    if (!this.urlGroupInfo) return
    switch (this.urlGroupInfo.data.type) {
      case 'student': {
        const student = this.studentGroups?.getStudent(this.urlGroupInfo)
        if (!student) return
        return [student]
      }
      case 'grade':
      case 'lesson-class':
        return this.studentGroups?.getGroupFromGroupInfo(this.urlGroupInfo)?.students.toArr
    }
  }

  get groupStudentIdList() {
    return this.groupStudents?.map(({ id }) => id) || []
  }

  private _attendanceLastPath?: string // 출결 탭 마지막 경로

  get attendanceLastPath() {
    const { router } = commonRepo
    return (
      this._attendanceLastPath ??
      (this.isAttendancePath(router?.location.pathname ?? '') && router?.location.pathname)
    )
  }

  private get _fallbackPathFromStudentGroup() {
    // 첫번째 학년이 없을 시(재학중인 학생이 아무도 없을 때)
    if (!this.studentGroups?.firstGradeGroup) return RouteName.attendanceManage

    const schoolGrade = SchoolGrade.fromKoLabel(this.studentGroups?.firstGradeGroup.id)
    // 학년 정보 만들기 실패시
    if (!schoolGrade) return RouteName.attendanceManage

    const groupInfo = new GroupInfo({
      type: 'grade',
      schoolGrade,
    })
    const fallbackPath = this.makeGroupInfoPathName({
      groupInfo,
      baseUrl: RouteName.attendance,
    })
    return fallbackPath
  }

  // Redirect 초기 경로
  get fallbackPath() {
    if (this.attendanceLastPath === RouteName.AcademyTuitionManage) {
      return this._fallbackPathFromStudentGroup
    }
    if (this.attendanceLastPath) {
      return this.attendanceLastPath
    }

    const pathData = this.getPathData(commonRepo.router.location.pathname)

    if (pathData?.tab || pathData?.groupType || pathData?.key) {
      return commonRepo.router.location.pathname
    }
    return this._fallbackPathFromStudentGroup
  }

  setAttendanceType(viewType: 'chart' | 'calendar') {
    runInAction(() => {
      this.viewType = viewType
    })
    const {
      location: { pathname },
    } = commonRepo.router.history
    const [, , , , groupType, key] = pathname.split('/')
    const replaceUrl = generatePath(RouteName.attendanceManage, {
      groupType,
      key,
      type: this.viewType,
    })
    commonRepo.router.history.push(replaceUrl)
  }

  setAttendanceLastPath(fullPath: string) {
    if (!this.isAttendancePath(fullPath) || this._attendanceLastPath === fullPath) return
    this._attendanceLastPath = fullPath
  }

  // 출결 URL 생성
  makeGroupInfoPathName = ({ groupInfo, baseUrl }: { groupInfo?: GroupInfo; baseUrl?: string }) => {
    const newPathname = `${baseUrl}/manage/${groupInfo?.data.type}/${groupInfo?.strKey}/${this.viewType}`
    return newPathname
  }

  // 출결 페이지인지
  isAttendancePath(pathname: string) {
    return pathname.includes('/attendance/')
  }

  getPathData(pathname: string) {
    if (this.isAttendancePath(pathname)) {
      const match = pathname.split('/')
      if (match) {
        const [, , , tab, groupType, key, viewType = this.viewType] = match
        return {
          tab,
          groupType,
          key,
          viewType,
        }
      }
    }
  }

  // 최초 1회 - 캐싱데이터 가져옴
  async fetchStudentGroups() {
    const studentGroups = await studentController.fetchStudentGroups()
    runInAction(() => (this.studentGroups = studentGroups))
  }
}
